import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useGetSiteArticleSettingsQuery } from '../../../../../api'
import { useAppDispatch } from '../../../../../store/hooks/Redux'
import { setModal } from '../../../../../store/reducers/AppSlice'
import { FeedbackModal } from '../../../ui/FeedbackModal'
import { GuideModal } from '../../../ui/GuideModal'
import { GiveFeedbackIcon } from '../../../../../../shared/components/icons/nav/GiveFeedbackIcon'
import { getNavItemClassName } from './NavItemClassName'
import { NavSubItemMarker } from './NavSubItemMarker'

export interface IHelpMenuProps {
  /** Пункты на светлой подложке — в поповере свёрнутого меню */
  light?: boolean
  /** Закрыть поповер после выбора пункта */
  onSelect?: () => void
}

export const HelpMenu = ({ light = false, onSelect }: IHelpMenuProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { data } = useGetSiteArticleSettingsQuery()
  const items = data?.data?.items

  const itemClassName = getNavItemClassName({ light, nested: true })
  const fieldClassName =
    'flex h-[34px] items-center justify-center rounded-[6px] bg-[#A059FF] px-[16px] text-center text-[13px] text-white transition-all hover:text-white hover:opacity-80'
  const contactClassName = clsx('text-[13px] transition-colors', {
    ['text-[#0E63D8] hover:opacity-80']: light,
    ['text-[#7AB7FF] hover:opacity-80']: !light,
  })

  const openModal = (children: JSX.Element, id?: string) => {
    dispatch(setModal({ ...(id && { id, size: 'xl', scroll: true }), children }))
    onSelect?.()
  }

  return (
    <div className={'flex flex-col gap-[8px]'}>
      <div className={'flex flex-col'}>
        {process.env.REACT_APP_PROJECT === 'bluepear' && (
          <button
            className={itemClassName}
            onClick={() => openModal(<GuideModal />, 'guide-modal')}
          >
            <NavSubItemMarker light={light} />
            {t('components.guideButton.quickGuide')}
          </button>
        )}
        {items?.articles?.map(({ name, url }) => (
          <a
            key={url}
            className={itemClassName}
            href={url}
            rel={'noreferrer'}
            target={'_blank'}
            onClick={() => onSelect?.()}
          >
            <NavSubItemMarker light={light} />
            <span className={'truncate'}>{name}</span>
          </a>
        ))}
      </div>
      {!!(items?.buttons?.length || items?.contacts?.length) && (
        <div className={'flex flex-col gap-[8px] rounded-[12px] bg-[#8D7CA71F] p-[12px]'}>
          <span
            className={clsx('text-center text-[13px] font-medium', {
              ['text-[#00326D]']: light,
              ['text-white']: !light,
            })}
          >
            {t('components.guideButton.askSpecialist')}
          </span>
          {items?.buttons?.map(({ name, url }) => (
            <a
              key={url}
              className={fieldClassName}
              href={url}
              rel={'noreferrer'}
              target={'_blank'}
              onClick={() => onSelect?.()}
            >
              {name}
            </a>
          ))}
          {!!items?.contacts?.length && (
            <div className={'flex justify-center gap-[12px]'}>
              {items.contacts.map(({ name, url }) => (
                <a
                  key={url}
                  className={contactClassName}
                  href={url}
                  rel={'noreferrer'}
                  target={'_blank'}
                  onClick={() => onSelect?.()}
                >
                  {name}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      {items?.feedback && (
        <button className={itemClassName} onClick={() => openModal(<FeedbackModal />)}>
          <NavSubItemMarker icon={GiveFeedbackIcon} light={light} />
          {t('components.guideButton.giveFeedback')}
        </button>
      )}
    </div>
  )
}
